// What backs one figure on an owner statement.
//
// Clicking a number on the all-owners-by-month tab opens this. It answers four
// questions in the order an operator asks them: where did this come from, is it
// measured or modelled, what happened to VAT, and can I see the invoice.
//
// It is deliberately blunt. Most lines on an owner statement are modelled from a
// rule rather than receipted: insurance and telematics are standing costs,
// petrol is litres derived from a km-per-litre figure at a gazetted pump price,
// carwashes are a flat charge, the Fleetly fee is a percentage. Where nothing is
// held, this says "No supporting document held" in those words rather than
// leaving a blank, because a blank reads as fine and a sentence reads as work to
// do. That is the point of the screen: an operator should be able to see, in one
// pass down a column, how much of an owner's month is evidence and how much is
// arithmetic.
//
// Nothing here decides anything. Every sentence comes from
// public.v_statement_line_provenance through statements/getStatementLineProvenance,
// so the rule lives in one place and the PDF and the export can read the same
// one later. See CLAUDE.md rule 4.
import { useEffect, useState } from 'react'
import { FileCheck2, FileLock2, FileWarning, FileX2, Calculator, Sigma, Gauge, Receipt, ShieldCheck } from 'lucide-react'
import { useGetStatementLineProvenance, useGetStatementLineEvidence } from '../../hooks/backend/statements'
import { EvidenceExtractView } from './EvidenceExtractView'
import { Spinner } from '../../lib/shadcn/spinner'
import { formatZar } from '../../utils/statements'
import { cn } from '../../lib/shadcn/utils'

export type LineEvidence = {
  line_code: string
  evidence_token: string | null
  evidence_title: string | null
  evidence_rows: number | null
  evidence_total_zar: number | null
  supplier: string | null
  source_covers_vehicles: number | null
  uber_shifts: number | null
  uber_validated_shifts: number | null
  uber_validated_pct: number | null
  unvalidated_reason: string | null
}

export type LineProvenance = {
  statement_id: string
  line_code: string
  amount_zar: number | null
  basis: string
  source_system: string
  vat_treatment: string
  evidence_status: string
  evidence_detail: string
  document_ref: string | null
  supplier: string | null
  xero_bill_ref: string | null
  attachment_filename: string | null
  gmail_thread_id: string | null
  document_count: number
  invoiced_incl_vat_zar: number | null
  invoice_variance_zar: number | null
  is_draft: boolean
}

// The basis chip. Wording is the operator's, not the schema's: "Modelled" is a
// warning and "Measured" is not, and an operator should be able to tell which
// without reading the sentence underneath.
const BASIS_LABEL: Record<string, string> = {
  measured: 'Measured',
  modelled: 'Modelled',
  rule: 'From a standing rule',
  invoiced: 'From an invoice',
  calculated: 'Calculated',
  policy: 'Policy decision',
  ledger: 'Carried from the ledger',
  derived: 'Subtotal',
}

const BASIS_TONE: Record<string, string> = {
  measured: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-950 dark:text-emerald-300',
  invoiced: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-950 dark:text-emerald-300',
  modelled: 'bg-amber-50 text-amber-800 ring-amber-600/20 dark:bg-amber-950 dark:text-amber-300',
  rule: 'bg-amber-50 text-amber-800 ring-amber-600/20 dark:bg-amber-950 dark:text-amber-300',
  calculated: 'bg-sky-50 text-sky-700 ring-sky-600/20 dark:bg-sky-950 dark:text-sky-300',
  policy: 'bg-violet-50 text-violet-700 ring-violet-600/20 dark:bg-violet-950 dark:text-violet-300',
  ledger: 'bg-slate-100 text-slate-700 ring-slate-600/20 dark:bg-slate-800 dark:text-slate-300',
  derived: 'bg-slate-100 text-slate-700 ring-slate-600/20 dark:bg-slate-800 dark:text-slate-300',
}

// The evidence line. Five of these ten states are a problem somebody has to fix,
// and they are coloured so that scanning a column shows where the holes are.
const EVIDENCE: Record<string, { label: string; tone: string; Icon: typeof FileCheck2 }> = {
  document_held: { label: 'Invoice held', tone: 'text-emerald-600 dark:text-emerald-400', Icon: FileCheck2 },
  document_unreadable: { label: 'Invoice held but unreadable', tone: 'text-amber-600 dark:text-amber-400', Icon: FileWarning },
  document_locked: { label: 'Invoice held but password protected', tone: 'text-amber-600 dark:text-amber-400', Icon: FileLock2 },
  never_received: { label: 'Invoice never received', tone: 'text-rose-600 dark:text-rose-400', Icon: FileX2 },
  itemised_no_invoice: { label: 'Itemised, no invoice attached', tone: 'text-amber-600 dark:text-amber-400', Icon: FileWarning },
  source_report: { label: 'From the source report', tone: 'text-emerald-600 dark:text-emerald-400', Icon: Receipt },
  driver_declaration: { label: 'Driver declaration', tone: 'text-amber-600 dark:text-amber-400', Icon: Gauge },
  calculated_from_rule: { label: 'No supporting document held', tone: 'text-muted-foreground', Icon: Calculator },
  derived: { label: 'Subtotal of the lines above', tone: 'text-muted-foreground', Icon: Sigma },
  no_document: { label: 'No supporting document held', tone: 'text-muted-foreground', Icon: FileX2 },
}

type Props = {
  statementId: string
  lineCode: string
  lineLabel: string
  plate: string
  ownerName: string
  periodLabel: string
}

export function StatementCellProvenance({
  statementId,
  lineCode,
  lineLabel,
  plate,
  ownerName,
  periodLabel,
}: Props) {
  const query = useGetStatementLineProvenance<LineProvenance | null>()
  const { trigger } = query
  const [failed, setFailed] = useState(false)

  // The proof is fetched separately from the explanation on purpose. A missing
  // document must never blank the sentence that says what the figure is, because
  // that sentence is what an operator needs while an owner is on the phone.
  const evidenceQuery = useGetStatementLineEvidence<LineEvidence[]>()
  const { trigger: triggerEvidence } = evidenceQuery
  const [showDoc, setShowDoc] = useState(false)

  useEffect(() => {
    let cancelled = false
    setFailed(false)
    setShowDoc(false)
    trigger({ statement_id: statementId, line_code: lineCode })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    triggerEvidence({ statement_id: statementId }).catch(() => {})
    return () => {
      cancelled = true
    }
  }, [trigger, triggerEvidence, statementId, lineCode])

  const evidence = (evidenceQuery.data ?? []).find((e) => e.line_code === lineCode) ?? null

  if (query.loading) {
    return (
      <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" />
        Reading what backs this figure
      </div>
    )
  }

  // A failure to read the provenance must never be shown as "no document held".
  // Those two sentences look the same on screen and mean opposite things.
  if (failed || query.error) {
    return (
      <div className="py-2 text-sm text-muted-foreground">
        Could not read what backs this figure just now. The figure itself is
        unaffected. Try again, and if it keeps failing say so rather than
        assuming there is nothing behind it.
      </div>
    )
  }

  const p = query.data
  if (!p) {
    return (
      <div className="py-2 text-sm text-muted-foreground">
        Nothing recorded about where this figure came from.
      </div>
    )
  }

  const basisLabel = BASIS_LABEL[p.basis] ?? p.basis
  const basisTone = BASIS_TONE[p.basis] ?? BASIS_TONE['derived']!
  const ev = EVIDENCE[p.evidence_status] ?? EVIDENCE['no_document']!
  const EvIcon = ev.Icon

  return (
    <div className="flex flex-col gap-3 text-sm">
      <div>
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-semibold">{lineLabel}</span>
          <span className="tabular-nums font-semibold">
            {p.amount_zar === null ? '—' : formatZar(p.amount_zar)}
          </span>
        </div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          {plate}
          {ownerName ? ` · ${ownerName}` : ''} · {periodLabel}
          {p.is_draft ? ' · draft' : ''}
        </div>
      </div>

      <div>
        <span
          className={cn(
            'inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1 ring-inset',
            basisTone,
          )}
        >
          {basisLabel}
        </span>
      </div>

      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Where it came from
        </div>
        <div className="mt-0.5">{p.source_system}</div>
      </div>

      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          VAT
        </div>
        <div className="mt-0.5">{p.vat_treatment}</div>
      </div>

      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Validation
        </div>
        <div className={cn('mt-0.5 flex items-start gap-1.5 font-medium', ev.tone)}>
          <EvIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{ev.label}</span>
        </div>
        <div className="mt-1 text-muted-foreground">{p.evidence_detail}</div>
      </div>

      {/* The invoice, and whether it agrees. A variance is shown whenever one can
          be computed, including zero, because "checked and it agrees" is a
          different and more useful statement than saying nothing. */}
      {p.document_count > 0 && (
        <div className="rounded border border-border bg-muted/30 p-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Document
          </div>
          <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-xs">
            {p.supplier && (
              <>
                <dt className="text-muted-foreground">Supplier</dt>
                <dd>{p.supplier}</dd>
              </>
            )}
            {p.document_ref && (
              <>
                <dt className="text-muted-foreground">Reference</dt>
                <dd className="break-all">{p.document_ref}</dd>
              </>
            )}
            {p.attachment_filename && (
              <>
                <dt className="text-muted-foreground">File</dt>
                <dd className="break-all">{p.attachment_filename}</dd>
              </>
            )}
            {p.xero_bill_ref && (
              <>
                <dt className="text-muted-foreground">Xero bill</dt>
                <dd className="break-all">{p.xero_bill_ref}</dd>
              </>
            )}
            {p.invoiced_incl_vat_zar !== null && (
              <>
                <dt className="text-muted-foreground">Invoiced</dt>
                <dd className="tabular-nums">{formatZar(p.invoiced_incl_vat_zar)} incl VAT</dd>
              </>
            )}
            {p.invoice_variance_zar !== null && (
              <>
                <dt className="text-muted-foreground">Variance</dt>
                <dd
                  className={cn(
                    'tabular-nums font-medium',
                    Math.abs(p.invoice_variance_zar) < 0.01
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-amber-600 dark:text-amber-400',
                  )}
                >
                  {Math.abs(p.invoice_variance_zar) < 0.01
                    ? 'Agrees with the invoice'
                    : `${formatZar(p.invoice_variance_zar)} against the invoice`}
                </dd>
              </>
            )}
          </dl>
          {p.gmail_thread_id && (
            <a
              className="mt-2 inline-block text-xs font-medium text-primary underline underline-offset-2"
              href={`https://mail.google.com/mail/u/0/#all/${p.gmail_thread_id}`}
              target="_blank"
              rel="noreferrer"
            >
              Open the email the invoice arrived on
            </a>
          )}
        </div>
      )}

      {/* How much of this month's earnings Uber actually stands behind. This used
          to be asserted in prose on every statement ever issued, including months
          where almost nothing could be matched, so it now counts. */}
      {evidence && evidence.uber_shifts !== null && evidence.uber_shifts > 0 && (
        <div
          className={cn(
            'flex items-start gap-2 rounded-md px-3 py-2 text-xs',
            (evidence.uber_validated_pct ?? 0) >= 95
              ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
              : 'bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
          )}
        >
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            <strong>
              {evidence.uber_validated_shifts} of {evidence.uber_shifts} shifts
            </strong>{' '}
            carry Uber&apos;s own figure ({evidence.uber_validated_pct ?? 0}%).
            {evidence.unvalidated_reason === 'no_uber_identity'
              ? ' The rest are on drivers not linked to an Uber identity, which no earnings report can fix: it needs the Uber Fleet driver roster.'
              : evidence.unvalidated_reason
                ? ' The rest rest on the shift submission alone.'
                : ''}
          </span>
        </div>
      )}

      {/* The document itself. Opened in place rather than in a new tab, because
          the question it answers is being asked about the number above it. */}
      {evidence?.evidence_token && (
        <div className="border-t border-border pt-2">
          {showDoc ? (
            <EvidenceExtractView token={evidence.evidence_token} />
          ) : (
            <button
              type="button"
              onClick={() => setShowDoc(true)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-primary underline underline-offset-2"
            >
              <Receipt className="h-3.5 w-3.5" />
              See the {evidence.evidence_rows ?? 0} line
              {evidence.evidence_rows === 1 ? '' : 's'} behind this figure
              {evidence.supplier ? ` (${evidence.supplier})` : ''}
              {evidence.source_covers_vehicles && evidence.source_covers_vehicles > 1
                ? `, this vehicle only`
                : ''}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
